import React from 'react';
import PropTypes from 'prop-types';
import View from 'components/view';
import Icon from 'components/icon';
import Link from 'components/link';
import ButtonLink from 'components/button/link';
import ExternalLink from 'components/link/external';
import ConsoleLink from 'components/console/link';
import DropdownButton from 'components/button/dropdown';
import StorefrontItem from 'components/storefront/storefront-item';
import SectionHeader from 'components/section-header';
import CollectionEmpty from 'components/collection/empty';
import Status from 'components/status';

import ThemeSupportAlert from './theme-support-alert';

import { STOREFRONT_BETA_FEEDBACK_URL } from 'constants/storefront';
import {
  getStorefrontURL,
  storefrontLimitReached,
  renderWithStorefrontLimitTooltip,
} from 'utils/storefront';

import './storefront.scss';

export default class StorefrontPage extends React.PureComponent {
  static contextTypes = {
    client: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    openModal: PropTypes.func.isRequired,
  };

  static propTypes = {
    storefronts: PropTypes.object.isRequired,
    onUploadThemeSuccess: PropTypes.func.isRequired,
  };

  getStorefrontUrlClean(storefront) {
    const storefrontUrl = getStorefrontURL(storefront);

    // Get rid of http/https prefix
    return storefrontUrl.replace(/^http(s)?:\/\//, '');
  }

  renderPrimaryStorefront(storefront) {
    const storefrontUrl = getStorefrontURL(storefront);
    const storefrontUrlClean = this.getStorefrontUrlClean(storefront);

    return (
      <div className="view-body-section storefront-primary">
        <div className="view-body-subheader">
          <SectionHeader
            className="view-body-subtitle"
            title={
              <ExternalLink to={storefrontUrl} icon={false}>
                {storefrontUrlClean}
              </ExternalLink>
            }
          />
          <div className="view-body-actions">
            {this.renderPrimaryActions(storefront)}
          </div>
        </div>
        <div className="view-section-content">
          {this.renderStorefront(storefront)}
        </div>
      </div>
    );
  }

  renderPrimaryActions(storefront) {
    const storefrontUrl = getStorefrontURL(storefront);

    return (
      <>
        <ButtonLink to="/storefronts/domains" type="secondary" size="sm">
          Domains
        </ButtonLink>
        <ButtonLink
          to={storefrontUrl}
          target="_blank"
          rel="noopener noreferrer"
          type="secondary"
          size="sm"
        >
          <Icon fa="eye" /> View store
        </ButtonLink>
      </>
    );
  }

  renderConsoleLink(storefront) {
    const { user } = this.context;
    if (!user.devtools) {
      return null;
    }

    return (
      <ConsoleLink
        key="console"
        model=":storefronts"
        uri={storefront.id}
        icon={false}
        buttonType={null}
      >
        <span className="with-icon">
          <Icon fa="rectangle-code" />
          View in console
        </span>
      </ConsoleLink>
    );
  }

  onClickUploadTheme = () => {
    const { onUploadThemeSuccess } = this.props;
    const { openModal } = this.context;

    openModal('UploadTheme', {
      createStorefront: true,
      onUpload: onUploadThemeSuccess,
    });
  };

  renderDevelopmentStorefrontList(devStorefronts) {
    const { client } = this.context;
    const { storefronts } = this.props;

    return (
      <div className="view-body-section storefront-development-section">
        <div className="view-body-subheader">
          <SectionHeader
            className="view-body-subtitle"
            title="Development storefronts"
            subtitle={
              devStorefronts.length > 0
                ? 'Try out new apps and themes, or test changes to your current storefront'
                : ''
            }
          />
          <div className="view-body-actions">
            {client.features.has('storefront_v2') ? (
              <DropdownButton
                key="actions"
                type="secondary"
                align="end"
                items={[
                  <Link key="browse" to="/storefronts/new">
                    <span className="with-icon">
                      <Icon fa="book-open-cover" />
                      Browse catalog
                    </span>
                  </Link>,
                  <hr key="hr" />,
                  renderWithStorefrontLimitTooltip(
                    storefronts,
                    (limitReached) => (
                      <button
                        key="upload"
                        onClick={this.onClickUploadTheme}
                        disabled={limitReached}
                      >
                        <span className="with-icon">
                          <Icon fa="upload" />
                          Upload theme
                        </span>
                      </button>
                    ),
                    { side: 'left' },
                  ),
                ]}
              >
                Add storefront
              </DropdownButton>
            ) : (
              <ButtonLink to="/storefronts/new" type="secondary">
                Add storefront
              </ButtonLink>
            )}
          </div>
        </div>
        <div className="view-section-content collection-full">
          {devStorefronts.length > 0 ? (
            <div className="collection-table-container">
              <table className="collection-table outer headless">
                <tbody>
                  {devStorefronts.map((storefront, index) => (
                    <tr key={index}>
                      <td>{this.renderStorefront(storefront)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <CollectionEmpty
              emptyTitle={false}
              emptyAction={false}
              emptyDescription="Try out new storefront apps and themes, or test changes to your current storefront."
            />
          )}
        </div>
      </div>
    );
  }

  renderStorefront(storefront) {
    return (
      <>
        {storefront.hosted && (
          <ThemeSupportAlert appearance="opaque" className="tight" />
        )}
        <StorefrontItem {...this.props} storefront={storefront} />
      </>
    );
  }

  render() {
    const { client } = this.context;
    const { storefronts } = this.props;

    const primaryStorefront = storefronts.results.find(
      (storefront) => storefront.primary,
    );
    const devStorefronts = storefronts.results.filter(
      (storefront) => !storefront.primary,
    );

    const storefrontLimit = storefrontLimitReached(storefronts);

    return (
      <View
        key="storefront"
        headerTitle="Storefront"
        headerSubtitle="Manage your storefront theme and settings"
        detail={true}
        headerActions={[]}
        overrideHelpLinkKey="storefronts"
        className="storefront-page"
      >
        {client.features.has('storefront_v2') && (
          <div className="storefront-beta-feedback-wrapper">
            <Status
              dot={false}
              prefixText={'V2 Beta'}
              faIcon={'megaphone'}
              type={null}
              className="storefront-beta-feedback"
              size="md"
            >
              <a
                href={STOREFRONT_BETA_FEEDBACK_URL}
                rel="noopener noreferrer"
                target="blank"
              >
                Give feedback
              </a>
            </Status>
          </div>
        )}
        {storefronts.results.length > 0 ? (
          <div className="view-body-section-group storefronts">
            {primaryStorefront &&
              this.renderPrimaryStorefront(primaryStorefront)}
            {this.renderDevelopmentStorefrontList(devStorefronts)}
            {storefrontLimit && (
              <div className="collection-pages">
                You've reached the limit of {storefrontLimit} storefronts.
              </div>
            )}
          </div>
        ) : (
          <CollectionEmpty
            emptyTitle="Create your storefront"
            emptyDescription="Choose a theme and launch an online store in minutes"
            emptyAction="Create storefront"
            uri="/storefronts"
          />
        )}
      </View>
    );
  }
}
